import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import AppShell from "../components/AppShell";
import { confirmSetupSession, getSetupSession, type SetupSessionInfo } from "../api/agents";

const REDIRECT_DELAY_MS = 2000;

export default function ConnectPC() {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const [session, setSession] = useState<SetupSessionInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [done, setDone] = useState<{ agentId: string; hostname: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!code) return;
    getSetupSession(code)
      .then(setSession)
      .catch(() => setError("This setup link is invalid or has expired."))
      .finally(() => setLoading(false));
  }, [code]);

  useEffect(() => {
    if (!done) return;
    const t = setTimeout(() => {
      navigate(`/computers/${done.agentId}`, { replace: true });
    }, REDIRECT_DELAY_MS);
    return () => clearTimeout(t);
  }, [done, navigate]);

  async function onConfirm() {
    if (!code) return;
    setConfirming(true);
    setError(null);
    try {
      const result = await confirmSetupSession(code);
      setDone({ agentId: result.agent_id, hostname: result.hostname });
    } catch (e: unknown) {
      setError(extractError(e));
    } finally {
      setConfirming(false);
    }
  }

  if (loading) {
    return (
      <AppShell>
        <div className="mx-auto max-w-lg pt-16 text-center text-slate-500">Loading...</div>
      </AppShell>
    );
  }

  if (error && !session) {
    return (
      <AppShell>
        <div className="mx-auto max-w-lg pt-16 text-center">
          <div className="rounded-2xl border border-red-200 bg-red-50 p-8">
            <p className="text-red-800">{error}</p>
            <Link to="/computers" className="mt-4 inline-block text-sm text-slate-600 hover:underline">
              Back to computers
            </Link>
          </div>
        </div>
      </AppShell>
    );
  }

  if (done) {
    return (
      <AppShell>
        <div className="mx-auto max-w-lg pt-16 text-center">
          <div className="rounded-2xl border border-green-200 bg-green-50 p-8">
            <div className="text-4xl">✓</div>
            <h1 className="mt-4 text-xl font-semibold text-slate-900">
              {done.hostname} is now connected!
            </h1>
            <p className="mt-2 text-sm text-slate-600">
              The agent is finishing setup. You'll see it come online here in a moment.
            </p>
            <Link
              to={`/computers/${done.agentId}`}
              replace
              className="mt-6 inline-block rounded-lg bg-slate-900 px-5 py-2.5 text-sm font-medium text-white hover:bg-slate-700"
            >
              View this computer
            </Link>
          </div>
        </div>
      </AppShell>
    );
  }

  const expired = session?.is_expired || session?.status === "expired";

  return (
    <AppShell>
      <div className="mx-auto max-w-lg pt-12">
        <Link to="/computers" className="text-sm text-slate-600 hover:underline">
          ← Back to computers
        </Link>

        <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-8">
          <div className="flex items-center gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
              <svg className="h-6 w-6 text-slate-500" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 17.25v1.007a3 3 0 0 1-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0 1 15 18.257V17.25m6-12V15a2.25 2.25 0 0 1-2.25 2.25H5.25A2.25 2.25 0 0 1 3 15V5.25m18 0A2.25 2.25 0 0 0 18.75 3H5.25A2.25 2.25 0 0 0 3 5.25m18 0H3" />
              </svg>
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-wider text-slate-500">
                New computer
              </p>
              <h1 className="text-xl font-semibold text-slate-900">{session?.hostname}</h1>
            </div>
          </div>

          <p className="mt-6 text-slate-600">
            This computer is waiting to join your office network. Click below to connect it.
          </p>

          {expired ? (
            <div className="mt-6 rounded-lg bg-amber-50 p-4 text-sm text-amber-900">
              This setup link has expired. Ask the user to run the installer again to get a new link.
            </div>
          ) : (
            <>
              {error ? (
                <p role="alert" className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-800">
                  {error}
                </p>
              ) : null}
              <button
                type="button"
                onClick={onConfirm}
                disabled={confirming}
                className="mt-6 w-full rounded-lg bg-slate-900 px-4 py-3 text-sm font-medium text-white disabled:opacity-50"
              >
                {confirming ? "Connecting..." : "Connect this computer to my office"}
              </button>
              <p className="mt-3 text-center text-xs text-slate-400">
                This will add {session?.hostname} to your office network and shared folder access.
              </p>
            </>
          )}
        </div>
      </div>
    </AppShell>
  );
}

function extractError(e: unknown): string {
  if (typeof e === "object" && e && "response" in e) {
    const r = (e as { response?: { data?: { detail?: string } } }).response;
    if (r?.data?.detail) return r.data.detail;
  }
  return "Could not connect this computer. Please try again.";
}
